import { getMenuItems } from '@/app/helpers/menu';
import { Component, inject, Input, type OnInit, type AfterViewInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { VerticalAppMenuComponent } from './vertical-menu/vertical-menu';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { RouterModule, Router, NavigationEnd } from '@angular/router';

@Component({
  selector: 'vertical-menu-button',
  standalone: true,
  imports: [VerticalAppMenuComponent, CommonModule, RouterModule],
  template: `
    <ng-template #offcanvasContent let-offcanvas>
      <div class="drawer-wrapper">
        <div class="drawer-header">
          <img src="assets/images/logo.svg" alt="Arcadia" class="drawer-logo" />
          <button class="drawer-close" (click)="offcanvas.close()">✕</button>
        </div>

        <div class="drawer-body">
          <vertical-app-menu [menuItems]="menuItems"></vertical-app-menu>

          <hr class="drawer-divider" />

          <ul class="navbar-nav flex-column">
            <li class="nav-item">
              <a class="nav-link drawer-link" routerLink="/auth/sign-in" (click)="offcanvas.close()">
                <i class="bi bi-box-arrow-in-right"></i>
                Iniciar sesión
              </a>
            </li>
          </ul>
        </div>
      </div>
    </ng-template>

    <li class="nav-item {{ navItemClass }}">
      <button
        class="navbar-toggler p-0 p-sm-2 menu-toggle-btn"
        [ngClass]="className"
        [style.--bs-navbar-color]="'#FFB03E'"
        data-bs-toggle="collapse"
        type="button"
        (click)="openDrawer(offcanvasContent)"
        data-bs-target="#navbarCollapse"
        aria-controls="navbarCollapse"
        [attr.aria-expanded]="isOffcanvasOpen"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-animation">
          <span></span>
          <span></span>
          <span></span>
        </span>
        @if (showText) {
          <span class="d-none ms-1 d-sm-inline-block small">Menu</span>
        }
      </button>
    </li>
  `,
  styles: `
    :host(vertical-menu-button) {
      display: contents;
    }

    .menu-toggle-btn {
      background: transparent;
      color: #FFB03E;
      position: relative;
    }

    .menu-toggle-btn::after {
      content: '';
      position: absolute;
      left: 8px;
      right: 8px;
      bottom: -2px;
      height: 2px;
      background: rgba(255, 176, 62, 1);
      border-radius: 1px;
      opacity: 0;
      transform: scaleX(0.9);
      transition: opacity .18s ease, transform .18s ease;
    }
    .menu-toggle-btn:hover::after,
    .menu-toggle-btn[aria-expanded='true']::after {
      opacity: 1;
      transform: scaleX(1);
    }

    .drawer-wrapper {
      display: flex;
      flex-direction: column;
      background: #ffffff;
      border-radius: 0 0 20px 20px;
      overflow: hidden;
    }

    .drawer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 18px 24px;
      background: linear-gradient(90.49deg, #0891B2 -37.06%, #0D1A47 131.24%);
    }

    .drawer-logo {
      height: 36px;
      width: auto;
    }

    .drawer-close {
      background: rgba(255, 255, 255, 0.18);
      border: 1.5px solid rgba(255, 255, 255, 0.35);
      border-radius: 50%;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: #ffffff;
      cursor: pointer;
      transition: background 0.2s ease;
    }

    .drawer-close:hover {
      background: rgba(255, 255, 255, 0.28);
    }

    .drawer-body {
      padding: 12px 0 20px;
    }

    .drawer-divider {
      border-color: rgba(13, 26, 71, 0.3);
      border-top-width: 1px;
      margin: 8px 20px;
    }

    .drawer-nav-item {
      margin: 2px 8px;
    }

    .drawer-link {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 15px;
      font-weight: 400;
      color: #0d1a47 !important;
      border-radius: 12px;
      padding: 6px 20px;
      margin: 2px 8px;
      border: 1.5px solid transparent;
      display: flex;
      align-items: center;
      gap: 14px;
      transition: background 0.2s ease, color 0.2s ease;
    }

    .drawer-link:hover {
      background: rgba(8, 145, 178, 0.06);
      color: #0891b2 !important;
    }

    .drawer-link i {
      font-size: 18px;
      width: 20px;
      text-align: center;
      flex-shrink: 0;
    }
  `,
})
export class VerticalMenuButtonComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() showText: boolean = true;
  @Input() className: string = 'true';
  @Input() navItemClass: string = 'ms-2 ms-md-3';

  offcanvasService = inject(NgbOffcanvas);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();
  isOffcanvasOpen: boolean = false;

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.offcanvasService.dismiss();
      });
  }

  ngAfterViewInit(): void {
    this.offcanvasService.activeInstance
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => {
        setTimeout(() => {
          this.isOffcanvasOpen = Boolean(e);
          this.cdr.markForCheck();
        }, 0);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openDrawer(content: any) {
    this.offcanvasService.open(content, {
      position: 'top',
      panelClass: 'drawer-panel',
      backdrop: true,
      scroll: false,
    });
  }

  menuItems = getMenuItems();
}